import type {
  GenericTrackingEvent,
  GlycerinProductClickPayload,
  GtmEventEECproductClick,
} from '@packages/tracking';
import { GlycerinPlacement, useTracking } from '@packages/tracking';

import type { CurrentNavigation, ProductVariation } from '../../api/rest/types';

import type { ActualSearchProductCardProps } from './SearchProductCard';

export const useProductClickTracking = (
  styleData: Pick<
    ActualSearchProductCardProps['product'],
    'masterSku' | 'brand' | 'title' | 'productPosition'
  >,
  variationData: Pick<ProductVariation, 'artNo' | 'price' | 'sku'>,
  categoryData: {
    breadcrumbData?: CurrentNavigation[];
    currentCategoryName?: string;
  },
) => {
  const { brand, title, productPosition, masterSku } = styleData;
  const { artNo, price, sku } = variationData;
  const { breadcrumbData, currentCategoryName } = categoryData;

  const dispatchGTMEvent = useTracking();

  const filteredBreadCrumb = breadcrumbData
    ? breadcrumbData.filter((item) => item.uriPath !== '/')
    : [];
  const customCategory = [...filteredBreadCrumb.map(({ name }) => name)].join('/');

  const dispatchProductClickTracking = () => {
    dispatchGTMEvent<GenericTrackingEvent>({
      event: 'suche-event',
      eventValue: `click=${productPosition};${artNo}`,
    });
    dispatchGTMEvent<GtmEventEECproductClick>({
      event: 'EECproductClick',
      ecommerce: {
        click: {
          actionField: {
            list: currentCategoryName ? `Category: ${currentCategoryName}` : 'Search Result',
            action: 'click',
          },
          products: [
            {
              name: title,
              id: artNo,
              price: price.value,
              category: customCategory,
              brand,
              position: productPosition ?? 0,
            },
          ],
        },
      },
    });
  };

  const productClickTrackingProps: GlycerinProductClickPayload = {
    category: 'product-click',
    productId: masterSku,
    articleId: artNo,
    detail: `${brand} ${title}`,
    listPosition: productPosition,
    skuId: sku,
    listId: currentCategoryName || 'Search Result',
    placement: GlycerinPlacement.LISTING,
  };

  return {
    dispatchProductClickTracking,
    productClickTrackingProps,
  };
};
